import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  LogOut,
  ArrowUpRight,
  FileSpreadsheet,
  Activity,
  Wallet,
  Inbox,
  CreditCard,
} from "lucide-react";

// REPLACE this with your Stripe Customer Portal URL:
// Stripe Dashboard → Settings → Billing → Customer Portal → "Login link"
const STRIPE_PORTAL_URL = "https://billing.stripe.com/p/login/REPLACE_ME";

const TILES = [
  {
    id: "jib",
    icon: FileSpreadsheet,
    label: "JIB & Cost Coding",
    metric: "0 open",
    desc: "Allocate field expenses, generate JIB statements, export to owners.",
  },
  {
    id: "los",
    icon: Activity,
    label: "Lease Operating Statements",
    metric: "Live",
    desc: "Track lifting cost vs revenue across every active well.",
  },
  {
    id: "orri",
    icon: Wallet,
    label: "Revenue & ORRI",
    metric: "Next run · TBD",
    desc: "Calculate royalties, deduct taxes, run ACH and check distributions.",
  },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleLogout = async () => {
    setBusy(true);
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F4F3EE] text-[#111418]" data-testid="dashboard-page">
      <header className="border-b border-[#D3D9DE] bg-[#F4F3EE]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="gp-container h-16 md:h-20 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="dashboard-logo"
            className="flex items-center gap-2 group"
          >
            <span aria-hidden className="inline-block h-2.5 w-2.5 bg-[#D67229]" />
            <span className="font-display text-2xl font-bold tracking-tight">GushPay</span>
          </button>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-3">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user?.name || "Operator"}
                  className="h-8 w-8 object-cover border border-[#111418]"
                />
              ) : (
                <span className="h-8 w-8 bg-[#0F4C5C] text-white flex items-center justify-center font-mono text-xs">
                  {(user?.name || user?.email || "G").slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="leading-tight">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#2C333A]">
                  Signed in
                </p>
                <p
                  className="text-sm text-[#111418] font-medium max-w-[200px] truncate"
                  data-testid="dashboard-user-email"
                >
                  {user?.email}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              disabled={busy}
              data-testid="dashboard-logout"
              className="gp-btn gp-btn-ghost disabled:opacity-60"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="gp-container py-16 md:py-24">
        {user?.is_admin && (
          <div className="mb-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
            <button
              type="button"
              onClick={() => navigate("/admin/stripe")}
              data-testid="dashboard-admin-stripe-link"
              className="flex items-center gap-3 border border-[#111418] bg-white px-5 py-4 hover:bg-[#111418] hover:text-[#F4F3EE] transition-colors group text-left"
            >
              <Inbox size={16} className="text-[#D67229] group-hover:text-[#E88B46]" />
              <div className="flex-1">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em]">
                  Admin · Subscribers &amp; revenue
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] opacity-60">
                  Live from Stripe
                </p>
              </div>
              <ArrowUpRight size={14} />
            </button>
            <button
              type="button"
              onClick={() => navigate("/admin/leads")}
              data-testid="dashboard-admin-link"
              className="flex items-center gap-3 border border-[#D3D9DE] bg-white px-5 py-4 hover:border-[#111418] transition-colors group text-left"
            >
              <Inbox size={16} className="text-[#8593A1] group-hover:text-[#D67229]" />
              <div className="flex-1">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C333A]">
                  Admin · Legacy demo leads
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8593A1]">
                  Historical form submissions
                </p>
              </div>
              <ArrowUpRight size={14} className="text-[#8593A1]" />
            </button>
          </div>
        )}
        <div className="grid grid-cols-12 gap-y-6 md:gap-x-12">
          <div className="col-span-12 md:col-span-7">
            <span className="gp-overline">// Operator Workspace</span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Welcome back,{" "}
              <span className="italic text-[#0F4C5C]">
                {user?.name?.split(" ")[0] || "Operator"}.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-[#2C333A] leading-relaxed">
              Your production accounting cockpit. Cost coding, LOS, and revenue
              runs in one place — the full workflow is rolling out to your
              account next.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 md:justify-self-end self-end">
            <div className="border border-[#111418] bg-white p-6 shadow-[8px_8px_0_0_#0F4C5C] max-w-sm">
              <span className="gp-overline">You&#39;re in</span>
              <p className="mt-3 font-display text-2xl text-[#111418] leading-tight">
                Everything unlocked. No wait.
              </p>
              <p className="mt-3 text-sm text-[#2C333A] leading-relaxed">
                Head to Modules to start plugging in your wells. No onboarding
                call, no setup fees, no waiting — you&#39;re fully live.
              </p>
            </div>
          </div>
        </div>

        <section className="mt-16 md:mt-24">
          <div className="flex items-end justify-between mb-8 border-b border-[#D3D9DE] pb-4">
            <h2 className="font-display text-2xl md:text-3xl text-[#111418]">
              Modules
            </h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2C333A]">
              3 / 3 ready · pilot
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TILES.map((tile) => {
              const Icon = tile.icon;
              return (
                <article
                  key={tile.id}
                  data-testid={`dashboard-tile-${tile.id}`}
                  className="bg-white border border-[#D3D9DE] p-8 flex flex-col h-full transition-all duration-300 hover:-translate-y-1 hover:border-[#D67229] hover:shadow-[12px_12px_0_0_#082933]"
                >
                  <div className="flex items-center justify-between">
                    <div className="h-11 w-11 border border-[#111418] flex items-center justify-center text-[#0F4C5C]">
                      <Icon size={18} strokeWidth={1.6} />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#D67229]">
                      {tile.metric}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-xl text-[#111418] leading-tight">
                    {tile.label}
                  </h3>
                  <p className="mt-3 text-sm text-[#2C333A] leading-relaxed">
                    {tile.desc}
                  </p>
                  <button
                    type="button"
                    className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#0F4C5C] hover:text-[#D67229] transition-colors self-start"
                  >
                    Open module <ArrowUpRight size={14} />
                  </button>
                </article>
              );
            })}
          </div>
        </section>

        {/* Billing */}
        <section className="mt-16 md:mt-20" data-testid="dashboard-billing-section">
          <div className="flex items-end justify-between mb-6 border-b border-[#D3D9DE] pb-4">
            <h2 className="font-display text-2xl md:text-3xl text-[#111418]">
              Billing
            </h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2C333A]">
              Powered by Stripe
            </span>
          </div>
          <div className="bg-white border border-[#D3D9DE] p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-4 max-w-xl">
              <div className="h-11 w-11 border border-[#111418] flex items-center justify-center text-[#0F4C5C] flex-shrink-0">
                <CreditCard size={18} strokeWidth={1.6} />
              </div>
              <div>
                <p className="font-display text-xl text-[#111418] leading-tight">
                  Need to update your billing or cancel your trial?
                </p>
                <p className="mt-2 text-sm text-[#2C333A] leading-relaxed">
                  Manage your subscription, swap payment methods, download
                  invoices, or pause your plan — all from your Stripe customer
                  portal.
                </p>
              </div>
            </div>
            <a
              href={STRIPE_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dashboard-manage-subscription"
              className="gp-btn gp-btn-primary flex-shrink-0"
            >
              Manage Subscription
              <ArrowUpRight size={16} />
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
